import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { getAccountingBasisLabel } from "@common/helpers/accountingBasis";
import { Ledger } from "@common/contracts/ledger.contract";

interface MultiEntityLedgerOption {
    value: string;
    label: string;
}

interface ResolveMultiEntityLedgerParams {
    // Ledgers for every accessible business entity, so the current ledger's
    // basis can be read even when it belongs to a no-longer-selected entity.
    ledgersByEntityId: Record<number, Ledger[]>;
    selectedEntityIds: number[];
    currentLedgerId?: string;
    // Carried across reports; preferred over Cash when the current ledger isn't selectable.
    preferredBasis?: AccountingLedgerBasis;
}

interface MultiEntityLedgerResolution {
    resolvedLedgerId: string | undefined;
    options: MultiEntityLedgerOption[];
}

// The backend joins multi-entity GL by ledger name, so only names present in
// every selected entity's ledger list form a valid selection.
function computeSharedLedgers(
    ledgersByEntityId: Record<number, Ledger[]>,
    selectedEntityIds: number[],
): Ledger[] {
    const selectedLedgerLists = selectedEntityIds.map(
        (entityId) => ledgersByEntityId[entityId] ?? [],
    );
    if (
        selectedLedgerLists.length === 0 ||
        selectedLedgerLists.some((ledgers) => ledgers.length === 0)
    ) {
        return [];
    }
    const [firstEntityLedgers, ...otherEntityLedgers] = selectedLedgerLists;
    return firstEntityLedgers.filter((ledger) =>
        otherEntityLedgers.every((ledgers) =>
            ledgers.some((other) => other.name === ledger.name),
        ),
    );
}

interface MultiEntityBasisInfo {
    cashLedgerId: string | undefined;
    accrualLedgerId: string | undefined;
    accrualLedgerName: string | undefined;
}

// Exposes the accrual ledger name so entity selection can be gated to entities
// that share it.
export function getMultiEntityBasisInfo({
    ledgersByEntityId,
    selectedEntityIds,
}: {
    ledgersByEntityId: Record<number, Ledger[]>;
    selectedEntityIds: number[];
}): MultiEntityBasisInfo {
    const sharedLedgers = computeSharedLedgers(
        ledgersByEntityId,
        selectedEntityIds,
    );
    const cashLedger = sharedLedgers.find(
        (ledger) => ledger.basis === AccountingLedgerBasis.cash,
    );
    const accrualLedger = sharedLedgers.find(
        (ledger) => ledger.basis === AccountingLedgerBasis.accruals,
    );
    return {
        cashLedgerId: cashLedger?.id,
        accrualLedgerId: accrualLedger?.id,
        accrualLedgerName: accrualLedger?.name,
    };
}

export function getEntityIdsWithLedgerName({
    ledgersByEntityId,
    entityIds,
    ledgerName,
}: {
    ledgersByEntityId: Record<number, Ledger[]>;
    entityIds: number[];
    ledgerName: string;
}): number[] {
    return entityIds.filter((entityId) =>
        (ledgersByEntityId[entityId] ?? []).some(
            (ledger) => ledger.name === ledgerName,
        ),
    );
}

/**
 * Resolves the single `ledgerId` sent to the multi-entity General Ledger
 * report. The backend resolves each entity's ledger by name, so a valid
 * selection must reference a ledger name shared across all selected entities,
 * and the representative `ledgerId` must belong to one of them (otherwise the
 * report 404s with "Ledger not accessible"). The previously selected basis is
 * preserved when available across the new selection, falling back to cash.
 */
export function resolveMultiEntityLedger({
    ledgersByEntityId,
    selectedEntityIds,
    currentLedgerId,
    preferredBasis,
}: ResolveMultiEntityLedgerParams): MultiEntityLedgerResolution {
    const selectedLedgerLists = selectedEntityIds.map(
        (entityId) => ledgersByEntityId[entityId] ?? [],
    );

    const sharedLedgers = computeSharedLedgers(
        ledgersByEntityId,
        selectedEntityIds,
    );

    if (sharedLedgers.length === 0) {
        return { resolvedLedgerId: undefined, options: [] };
    }

    const options = sharedLedgers.map((ledger) => ({
        value: ledger.id,
        label: ledger.name || getAccountingBasisLabel(ledger.basis),
    }));

    const currentLedger = Object.values(ledgersByEntityId)
        .flat()
        .find((ledger) => ledger.id === currentLedgerId);

    // Keep the current ledger as-is when it still belongs to one of the
    // selected entities and its name is shared across all of them. This makes
    // the result independent of `selectedEntityIds` ordering, so merely
    // reordering the same selection doesn't churn the ledgerId / refetch.
    const sharedLedgerNames = new Set(
        sharedLedgers.map((ledger) => ledger.name),
    );
    const isCurrentLedgerSelectable =
        currentLedger !== undefined &&
        sharedLedgerNames.has(currentLedger.name) &&
        selectedLedgerLists.some((ledgers) =>
            ledgers.some((ledger) => ledger.id === currentLedgerId),
        );
    if (isCurrentLedgerSelectable && currentLedgerId) {
        return { resolvedLedgerId: currentLedgerId, options };
    }

    // Otherwise pick a representative ledger that belongs to a selected entity,
    // preserving the previously selected basis/name when possible and falling
    // back to cash.
    const chosenLedger =
        sharedLedgers.find((ledger) => ledger.name === currentLedger?.name) ??
        (preferredBasis
            ? sharedLedgers.find((ledger) => ledger.basis === preferredBasis)
            : undefined) ??
        sharedLedgers.find(
            (ledger) => ledger.basis === AccountingLedgerBasis.cash,
        ) ??
        sharedLedgers[0];

    return { resolvedLedgerId: chosenLedger.id, options };
}
